import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { getSecretMeta, consumeSecret, translateApiError } from "@/lib/api";
import { decryptSecret } from "@/lib/crypto";
import { NotFoundState, BurnedState } from "@/components/view/StatusPanels";
import RevealPanel from "@/components/view/RevealPanel";
import RevealedPanel from "@/components/view/RevealedPanel";

const STATUS = {
  LOADING: "loading",
  NEEDS_PASSWORD: "needs-password",
  READY: "ready",
  REVEALED: "revealed",
  BURNED: "burned",
  NOTFOUND: "notfound",
};

function statusFromError(err, t) {
  const httpStatus = err?.response?.status;
  if (httpStatus === 404) return { status: STATUS.NOTFOUND, error: "" };
  return { status: null, error: translateApiError(t, err) };
}

export default function ViewSecret() {
  const { id } = useParams();
  const { t } = useLang();
  const [status, setStatus] = useState(STATUS.LOADING);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [plaintext, setPlaintext] = useState("");
  const [revealing, setRevealing] = useState(false);
  const [confirmedBurn, setConfirmedBurn] = useState(false);
  const keyFragment = typeof window !== "undefined" ? window.location.hash.slice(1) : "";

  useEffect(() => {
    setStatus(STATUS.LOADING);
    setMeta(null);
    setError("");
    setPassword("");
    setPlaintext("");
    setConfirmedBurn(false);
    if (!keyFragment) {
      setStatus(STATUS.NOTFOUND);
      setError(t("invalidUrl"));
      return;
    }
    getSecretMeta(id)
      .then((m) => {
        setMeta(m);
        setStatus(m.has_password ? STATUS.NEEDS_PASSWORD : STATUS.READY);
      })
      .catch((err) => {
        setStatus(STATUS.NOTFOUND);
        setError(err?.response?.status === 404 ? t("notFound") : translateApiError(t, err));
      });
  }, [id, keyFragment, t]);

  const handleReveal = useCallback(async () => {
    if (meta?.has_password && !password) return;
    if (meta?.one_time && !confirmedBurn) return;
    setRevealing(true);
    setError("");
    try {
      const data = await consumeSecret(id, meta?.has_password ? password : null);
      const pt = await decryptSecret(data.ciphertext, data.iv, keyFragment);
      setPlaintext(pt);
      setStatus(STATUS.REVEALED);
    } catch (err) {
      const httpStatus = err?.response?.status;
      if (httpStatus === 403) {
        setError(t("wrongPassword"));
      } else if (httpStatus === 404) {
        setStatus(STATUS.BURNED);
      } else if (err?.name === "OperationError" || err?.message?.includes("decrypt")) {
        setError(t("decryptError"));
      } else {
        const next = statusFromError(err, t);
        if (next.status) setStatus(next.status);
        setError(next.error);
      }
    } finally {
      setRevealing(false);
    }
  }, [id, meta, password, confirmedBurn, keyFragment, t]);

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="view-secret">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-black dark:hover:text-white mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("brand")}
      </Link>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#00ff66] mb-4">
        <Lock className="w-3.5 h-3.5" /> encrypted payload
      </div>
      <h2 className="text-3xl md:text-5xl font-black tracking-tighter text-neutral-900 dark:text-white mb-3">
        {t("viewTitle")}
      </h2>
      <p className="text-sm text-neutral-500 mb-10 max-w-xl leading-relaxed">
        {t("viewIntro")}
      </p>
      {status === STATUS.LOADING && (
        <div
          data-testid="loading-state"
          className="font-mono text-xs uppercase tracking-widest text-neutral-500 animate-pulse"
        >
          decoding meta...
        </div>
      )}
      {status === STATUS.NOTFOUND && <NotFoundState error={error} />}
      {status === STATUS.BURNED && <BurnedState />}
      {(status === STATUS.NEEDS_PASSWORD || status === STATUS.READY) && (
        <RevealPanel
          meta={meta}
          status={status}
          password={password}
          setPassword={setPassword}
          confirmedBurn={confirmedBurn}
          setConfirmedBurn={setConfirmedBurn}
          error={error}
          revealing={revealing}
          onReveal={handleReveal}
        />
      )}
      {status === STATUS.REVEALED && (
        <RevealedPanel plaintext={plaintext} oneTime={meta?.one_time} />
      )}
    </div>
  );
}
